/**
 * Mint Referrals Tab
 *
 * Shows on the Referrals page as the "Mint Referrals" tab.
 * Displays: balance, payout request, earnings by launch, payout history.
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../ui/components/Button';
import { FeatherDollarSign } from '@subframe/core';
import { getReferralBalance, requestReferralPayout, type ReferralBalanceResponse } from '../../api/referral-payouts';
import { useNotificationStore } from '../../store/notificationStore';
import { formatAddress } from '../../utils/formatters';

function fmtXrp(drops: number): string {
  const xrp = drops / 1_000_000;
  return xrp % 1 === 0 ? `${xrp} XRP` : `${parseFloat(xrp.toFixed(6))} XRP`;
}

export const MintReferralsTab: React.FC = () => {
  const [data, setData] = useState<ReferralBalanceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const { success: showSuccess, error: showError } = useNotificationStore();

  useEffect(() => {
    getReferralBalance()
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleRequestPayout = async () => {
    setRequesting(true);
    try {
      await requestReferralPayout();
      showSuccess('Payout requested! An admin will review it shortly.');
      // Refresh data
      const updated = await getReferralBalance();
      setData(updated);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to request payout');
    } finally {
      setRequesting(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-subtext-color">Loading mint referral data...</div>;
  }

  const balance = data?.balance;
  const earnings = data?.earnings || [];
  const payouts = data?.recent_payouts || [];
  const availableXrp = (balance?.available_balance_drops || 0) / 1_000_000;
  const totalEarnedXrp = (balance?.total_accrued_drops || 0) / 1_000_000;
  const totalPaidXrp = (balance?.total_paid_drops || 0) / 1_000_000;
  const canRequestPayout = availableXrp >= 1 && !data?.has_active_payout;

  return (
    <div className="flex w-full flex-col items-start gap-6">
      <p className="text-body font-body text-subtext-color">
        Share launch pages and earn a reward when someone mints through your link.
        Each launch sets its own referral percentage — you earn that percentage of
        the creator's mint revenue for every mint you refer.
      </p>

      {/* Balance Cards */}
      <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-lg border border-neutral-border bg-neutral-50 p-6">
          <div className="flex flex-col items-center gap-2">
            <span className="text-heading-2 font-heading-2 text-brand-600">
              {fmtXrp(balance?.available_balance_drops || 0)}
            </span>
            <span className="text-caption font-caption text-subtext-color">
              Available Balance
            </span>
          </div>
        </div>
        <div className="rounded-lg border border-neutral-border bg-neutral-50 p-6">
          <div className="flex flex-col items-center gap-2">
            <span className="text-heading-2 font-heading-2 text-default-font">
              {fmtXrp(balance?.total_accrued_drops || 0)}
            </span>
            <span className="text-caption font-caption text-subtext-color">
              Total Earned
            </span>
          </div>
        </div>
        <div className="rounded-lg border border-neutral-border bg-neutral-50 p-6">
          <div className="flex flex-col items-center gap-2">
            <span className="text-heading-2 font-heading-2 text-default-font">
              {fmtXrp(balance?.total_paid_drops || 0)}
            </span>
            <span className="text-caption font-caption text-subtext-color">
              Total Paid Out
            </span>
          </div>
        </div>
      </div>

      {/* Payout Request */}
      <div className="flex items-center gap-3">
        <Button
          icon={<FeatherDollarSign />}
          onClick={handleRequestPayout}
          loading={requesting}
          disabled={!canRequestPayout}
        >
          {data?.has_active_payout
            ? 'Payout Pending...'
            : `Request Payout (${fmtXrp(balance?.available_balance_drops || 0)})`}
        </Button>
        {availableXrp < 1 && !data?.has_active_payout && (
          <span className="text-caption font-caption text-subtext-color">
            Minimum payout: 1 XRP
          </span>
        )}
      </div>

      {/* Earnings by Launch */}
      {earnings.length > 0 && (
        <>
          <div className="flex h-px w-full flex-none bg-neutral-border" />
          <span className="text-caption font-caption text-subtext-color uppercase tracking-wider">
            EARNINGS BY LAUNCH
          </span>
          <div className="w-full overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-neutral-border">
                  <th className="text-left py-3 px-4 text-caption font-caption text-subtext-color uppercase tracking-wider">Launch</th>
                  <th className="text-right py-3 px-4 text-caption font-caption text-subtext-color uppercase tracking-wider">Reward</th>
                  <th className="text-right py-3 px-4 text-caption font-caption text-subtext-color uppercase tracking-wider">Mints</th>
                  <th className="text-right py-3 px-4 text-caption font-caption text-subtext-color uppercase tracking-wider">Earned</th>
                </tr>
              </thead>
              <tbody>
                {earnings.map((e) => (
                  <tr key={e.launch_id} className="border-b border-neutral-border hover:bg-neutral-50 transition-colors">
                    <td className="py-3 px-4 text-body font-body text-default-font">
                      {e.launch_slug ? (
                        <Link to={`/collections/${e.launch_slug}`} className="text-brand-600 hover:underline">{e.launch_name}</Link>
                      ) : e.launch_name}
                    </td>
                    <td className="py-3 px-4 text-right text-body font-body text-subtext-color">
                      {e.current_referral_bps ? `${e.current_referral_bps / 100}%` : '-'}
                    </td>
                    <td className="py-3 px-4 text-right text-body font-body text-default-font">{e.referred_mints}</td>
                    <td className="py-3 px-4 text-right text-body font-body text-brand-600">
                      {fmtXrp(e.total_earned_drops)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Payout History */}
      {payouts.length > 0 && (
        <>
          <div className="flex h-px w-full flex-none bg-neutral-border" />
          <span className="text-caption font-caption text-subtext-color uppercase tracking-wider">
            PAYOUT HISTORY
          </span>
          <div className="w-full overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-neutral-border">
                  <th className="text-left py-3 px-4 text-caption font-caption text-subtext-color uppercase tracking-wider">Date</th>
                  <th className="text-right py-3 px-4 text-caption font-caption text-subtext-color uppercase tracking-wider">Amount</th>
                  <th className="text-left py-3 px-4 text-caption font-caption text-subtext-color uppercase tracking-wider">Status</th>
                  <th className="text-left py-3 px-4 text-caption font-caption text-subtext-color uppercase tracking-wider">Tx Hash</th>
                  <th className="text-left py-3 px-4 text-caption font-caption text-subtext-color uppercase tracking-wider">Address</th>
                </tr>
              </thead>
              <tbody>
                {payouts.map((p) => (
                  <tr key={p.id} className="border-b border-neutral-border hover:bg-neutral-50 transition-colors">
                    <td className="py-3 px-4 text-body font-body text-subtext-color whitespace-nowrap">
                      {new Date(p.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4 text-right text-body font-body text-default-font whitespace-nowrap">
                      {fmtXrp(Number(p.amount_drops))}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`text-caption font-caption px-2 py-1 rounded ${
                        p.status === 'completed' ? 'bg-success-100 text-success-700' :
                        p.status === 'failed' ? 'bg-error-100 text-error-700' :
                        p.status === 'rejected' ? 'bg-error-100 text-error-700' :
                        'bg-neutral-100 text-subtext-color'
                      }`}>
                        {p.status === 'completed' ? 'Sent' : p.status.charAt(0).toUpperCase() + p.status.slice(1)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-body font-body text-subtext-color">
                      {p.tx_hash ? formatAddress(p.tx_hash) : '-'}
                    </td>
                    <td className="py-3 px-4 text-body font-body text-subtext-color">
                      {formatAddress(p.payout_address)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Empty State */}
      {earnings.length === 0 && (
        <div className="w-full flex flex-col items-center gap-4 py-8 text-center">
          <FeatherDollarSign className="w-12 h-12 text-neutral-400" />
          <div className="flex flex-col gap-2">
            <span className="text-body-bold font-body-bold text-default-font">
              No mint referrals yet
            </span>
            <span className="text-body font-body text-subtext-color max-w-md">
              Visit active launches with referral programs enabled, grab your referral link from the "Share & Earn" section, and share it with others.
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
